import { AlertCircle, AlertTriangle, Check } from "lucide-react";
import type { ComponentType, FC, ReactNode } from "react";

import type { OutgoingIntakeFormNode } from "@/api/types/outgoingIntake";
import type { PresendItem } from "@/features/requestsIde/editor/presend";
import { cn } from "@/lib/utils";

import type { BuilderSelection } from "../hooks/useBuilderSelection";
import { builderSections, issuesByNode } from "../model";

/** A titled block in the builder's side rail. */
export const RailSection: FC<{
  title: string;
  action?: ReactNode;
  children?: ReactNode;
}> = ({ title, action, children }) => (
  <section className="flex flex-col gap-2">
    <div className="flex h-6 items-center">
      <h3 className="flex-1 text-sm font-medium text-fg-3">{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

type Tone = "default" | "muted" | "danger" | "warn";

const TONE: Record<Tone, string> = {
  default: "text-fg-2 [&_[data-icon]]:text-fg-3",
  muted: "text-fg-3 [&_[data-icon]]:text-fg-3",
  danger: "text-danger",
  warn: "text-warn"
};

/** One line of facts in the rail: an icon and a sentence. */
export const RailLine: FC<{
  icon: ComponentType<{ className?: string }>;
  tone?: Tone;
  children?: ReactNode;
}> = ({ icon: Icon, tone = "default", children }) => (
  <span className={cn("flex items-start gap-2 text-sm", TONE[tone])}>
    <span data-icon className="inline-flex shrink-0 pt-0.5">
      <Icon className="size-3.5" />
    </span>
    <span className="min-w-0">{children}</span>
  </span>
);

/** The sections as a jump list; a red dot marks one with something to fix. */
export const SectionJumpList: FC<{
  schema: OutgoingIntakeFormNode[];
  selection: BuilderSelection;
  readOnly?: boolean;
}> = ({ schema, selection, readOnly }) => {
  const sections = builderSections(schema);
  const issues = readOnly ? new Map<string, string>() : issuesByNode(schema);
  const openSection = sections.find((s) =>
    s.steps.some((st) => st.node.node_id === selection.openId)
  )?.id;
  if (!sections.length)
    return <span className="text-sm text-fg-3">No sections yet.</span>;
  return (
    <nav className="-mx-2 flex flex-col">
      {sections.map((s) => {
        const flagged = s.steps.some((st) => issues.has(st.node.node_id));
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => {
              const first = s.steps[0]?.node.node_id;
              if (first) selection.jumpTo(first);
            }}
            className={cn(
              "flex h-7 items-center gap-2 rounded-md px-2 text-left text-base hover:bg-fill-1",
              openSection === s.id ? "bg-fill-2 text-fg" : "text-fg-2"
            )}
          >
            <span className="min-w-0 flex-1 truncate">{s.title}</span>
            {flagged ? (
              <span
                aria-label="Needs a fix"
                className="size-1.5 shrink-0 rounded-full bg-danger"
              />
            ) : (
              <span className="text-sm text-fg-4 tabular-nums">
                {s.steps.length}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
};

/** The pre-send checks: problems first (each jumps to its question), then what's fine. */
export const ChecksList: FC<{
  items: PresendItem[];
  onJump?: (nodeId: string) => void;
  /** Hide "ok" lines past the summary. */
  compact?: boolean;
}> = ({ items, onJump, compact }) => {
  const problems = items.filter((i) => i.severity !== "ok");
  const fine = items.filter((i) => i.severity === "ok");
  const shown = compact ? fine.slice(0, 1) : fine;
  return (
    <div className="flex flex-col gap-1.5">
      {problems.map((item) => {
        const line = (
          <RailLine
            icon={item.severity === "error" ? AlertCircle : AlertTriangle}
            tone={item.severity === "error" ? "danger" : "warn"}
          >
            {item.message}
          </RailLine>
        );
        return item.nodeId && onJump ? (
          <button
            key={`${item.code}|${item.nodeId}`}
            type="button"
            className="rounded-sm text-left hover:opacity-80"
            onClick={() => onJump(item.nodeId as string)}
          >
            {line}
          </button>
        ) : (
          <div key={`${item.code}|${item.nodeId ?? ""}|${item.message}`}>
            {line}
          </div>
        );
      })}
      {shown.map((item) => (
        <RailLine
          key={`${item.code}|${item.nodeId ?? ""}|${item.message}`}
          icon={Check}
          tone="muted"
        >
          {item.message}
        </RailLine>
      ))}
    </div>
  );
};
